import React from "react";
import { Container, River } from "@web-monorepo/mds";
import type { HeadlineProps } from "@web-monorepo/mds";
import ImageWithOptimizedVariants from "@src/components/ImageWithOptimizedVariants";

// Single composed illustration (padlock + privacy seals + Mojo), the same hosted
// asset used by /plus and /families.
const PRIVACY_ILLUSTRATION = "https://static.classdojo.com/img/external/privacy-security.svg";

export interface PrivacySectionCTA {
  label: string;
  href: string;
  onClick?: () => void;
  /** Button styling — the only thing that differs per page (e.g. /plus uses className "bg-gradient-plus"). */
  variant?: "primary" | "secondary" | "media" | "accent";
  tone?: "blue" | "green" | "orange" | "red" | "yellow";
  context?: "default" | "onAccentDark" | "onAccentLight";
  className?: string;
}

export interface PrivacySectionProps {
  headline: React.ReactNode;
  headlineWeight?: HeadlineProps["weight"];
  body: React.ReactNode;
  cta: PrivacySectionCTA;
  className?: string;
  /**
   * Grid placement for the River, for pages whose other Rivers sit on a
   * different inset than the default one (e.g. /families puts every River on
   * `lg:col-span-10 lg:col-start-2`). When set, the Container becomes an
   * `mds-grid` and the River is placed on it directly, replacing the default
   * `lg:px-col-width-0.75` wrapper.
   */
  riverClassName?: string;
}

/**
 * Shared privacy section — an MDS River with the composed privacy illustration.
 *
 * By default this mirrors the layout used on /plus and /teachers (Container +
 * lg:px-col-width-0.75 + River layout "1/1"); pass `riverClassName` to place
 * the River on the container grid instead, for pages whose other Rivers use a
 * different inset (see /families' ConnectSection).
 *
 * Note: pass `headline`/`body` as already-translated nodes and `cta.label` as a
 * raw string (River actions require a string label — use react-i18next `tRaw`).
 */
export const PrivacySection: React.FC<PrivacySectionProps> = ({
  headline,
  headlineWeight,
  body,
  cta,
  className,
  riverClassName,
}) => {
  const river = (
    <River
      className={riverClassName}
      layout="1/1"
      headline={headline}
      headlineSize="4"
      headlineWeight={headlineWeight}
      body={body}
      bodySize="1"
      media={<ImageWithOptimizedVariants src={PRIVACY_ILLUSTRATION} alt="ClassDojo privacy" />}
      action={{
        label: cta.label,
        href: cta.href,
        as: "a",
        onClick: cta.onClick,
        variant: cta.variant,
        tone: cta.tone,
        context: cta.context,
        className: cta.className,
      }}
    />
  );

  return (
    <Container className={`py-2xl ${className ?? ""}`} grid={Boolean(riverClassName)}>
      {riverClassName ? river : <div className="w-full lg:px-col-width-0.75">{river}</div>}
    </Container>
  );
};

export default PrivacySection;
